import { Card, Grid, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import SearchableCombobox from '../SearchableCombobox';
import Spinner from '../Spinner';
import TagsSelect from '../TagSelector';
import AlgorithmSelector from './AlgorithmSelector';
import NewProductsRuleSelector from './NewProductsRuleSelector';

const GeneralProductFeedForm = ({ productFeed, handleChange, setUserError }) => {
  const fetch = useFetch();
  const [loadError, setLoadError] = useState();
  const [eventActions, setEventActions] = useState();

  const { domain } = useSelector((state) => state.domain);

  // useMemo must be called unconditionally (before any early returns)
  const eventOptions = useMemo(
    () => (eventActions || []).map((action) => ({ label: action, value: action })),
    [eventActions],
  );

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/eventActions`)
      .then(({ eventActions }) => setEventActions(eventActions))
      .catch(setLoadError);
  }, [fetch, domain.accountId, domain.id]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (eventActions === undefined) {
    return <Spinner />;
  }

  const checkIfAlgorithmIsNew = () => {
    return productFeed.algorithm?.toLowerCase().includes('new') || false;
  };

  return (
    <Card withBorder>
      <Grid data-tour="productFeedName">
        <Grid.Col span={4}>
          <Text fw={500}>Name*</Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <TextInput
            name="name"
            type="text"
            value={productFeed.name}
            placeholder="e.g. Similar Products"
            onChange={(e) => handleChange(e)}
          />
        </Grid.Col>
      </Grid>
      <hr />
      <Grid data-tour="productFeedGeneralMode">
        <Grid.Col span={4}>
          <Text fw={500}>Scope*</Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <SegmentedControl
            value={productFeed.scope ?? ''}
            disabled={!!productFeed.id}
            onChange={(value) => handleChange({ target: { name: 'scope', value } })}
            data={[
              { label: 'Web', value: 'web' },
              { label: 'Marketing Channels', value: 'marketingChannels' },
              { label: 'Ads', value: 'ads' },
            ]}
          />
          <Text size="sm" c="dimmed">
            Product feeds for web can be configured to use the page context to offer a personalized experience. <br />
            You can use feeds meant for marketing channels on the web but not vice-verca. <br />
            Ads feeds use non-personalized algorithms (popular, trending) without user context.
          </Text>
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col span={4}>
          <Text fw={500}>Tags</Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <TagsSelect model={productFeed} handleChange={handleChange} setUserError={setUserError} />
        </Grid.Col>
      </Grid>
      <AlgorithmSelector
        labelCol={4}
        scope={productFeed.scope}
        algorithm={productFeed.algorithm}
        handleChange={handleChange}
      />
      {checkIfAlgorithmIsNew() && <NewProductsRuleSelector productFeed={productFeed} handleChange={handleChange} />}
      <Grid mt="md">
        <Grid.Col span={4}>
          <Text fw={500}>Secondary ranking*</Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <SegmentedControl
            value={productFeed.fallback ?? ''}
            onChange={(value) => handleChange({ target: { name: 'fallback', value: value || null } })}
            data={[
              { label: 'Most added to cart', value: 'popularScoreByCartAdd' },
              { label: 'Most viewed', value: 'popularScore' },
              { label: 'Newest', value: 'publishedAt' },
              // SegmentedControl only carries string values, so "no fallback" round-trips as ''.
              { label: 'None', value: '' },
            ]}
          />
          <Text size="sm" c="dimmed">
            This controls how products are sorted in case the chosen algorithm and boost / bury rules have insufficient
            data.
          </Text>
        </Grid.Col>
      </Grid>
      {productFeed.algorithm === 'latestEventProducts' && (
        <Grid mt="md">
          <Grid.Col span={4}>
            <Text fw={500}>Event</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <SearchableCombobox
              value={productFeed.contextAction}
              options={eventOptions}
              onChange={(value) => handleChange({ target: { name: 'contextAction', value } })}
            />
            <Text size="sm" c="dimmed">
              This determines which user action will determine the products to be returned. In addition to built-in
              events such as <strong>cartUpdate</strong>, <strong>cartCheckoutSuccess</strong> and friends, you can send{' '}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://releva.ai/documentation/third-party-integration-apis/custom-event-api/"
              >
                custom events to Releva <IconExternalLink size={14} />
              </a>{' '}
            </Text>
          </Grid.Col>
        </Grid>
      )}
    </Card>
  );
};

export default GeneralProductFeedForm;
